import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppIcons } from "./AppIcons";

// AppStanding - THE SUBSCRIBER'S OWN WHATSAPP ACCOUNT WARNING, AS RULES (owner-picked 2026-10-01: "Show account warnings
// in the app"). Habitat-free (no DOM, no transport): the Bridge imports it for the wire's shape, the suite drives it, and
// `AppStandingRun` is the browser half that paints the banner under the top bar.
//
// ⭐ WHAT IT IS: the service keeps one state word per subscriber (`Bridge/src/AccountStanding.ts`: `ok`, `limited`,
// `temporarily-banned`, `locked`, `banned`, `client-outdated`). Until now it was visible only on the unauthenticated
// `/status` (masked) and in the log. The chat list's long poll (`/live` mode `list`, proved with the subscriber's own
// `browser` key, their own tenant only) now carries it: the page sends what it SHOWS (`standing: "<sig>"`), the service
// answers `standing: {state, until?}` and answers a held request EARLY when its own state differs (`LiveRun`).
//
// ⛔ THE WIRE CARRIES A STATE WORD AND AN END TIME - NEVER A NUMBER, NEVER THE SERVICE'S LOG SENTENCE. The words the
// subscriber reads are written HERE, keyed on the state word (`Title`, `Words`), in plain language with what they can do.
// `Bridge/src/tests/StandingLive.test.ts` (CONTRACT, BOTH HALVES) pins every `AccountStanding` state to a sentence here.
// ⛔ NOT AN ERROR (law 1, fate 1): a restriction is a state of the subscriber's account that they can act on. It is shown,
// never reported - nothing here calls `KGlobalErrorHandler` for it and nothing latches.
// ⛔ NO LOOP: the page echoes back the signature of whatever state the service sent - a state word this build has no
// sentence for (a newer service) is still shown (a general sentence) and still echoed, so the service is not answered
// early again and again. An older page sends no `standing` and is never answered early for it.
// ⛔ READ-ONLY (decision 9): nothing here can send, and the composer is not blocked - the service decides what may go.

/** The account's standing as the page holds it. */
export class AppStandingNow {
    public State: string = "ok";
    /** When the restriction ends (ISO, UTC), when WhatsApp said; "" otherwise. */
    public Until: string = "";
}

/** What the banner says for one state. */
export class AppStandingText {
    public Title: string = "";
    public Words: string = "";
}

export class AppStanding {
    /** ⛔ The service's own words (`AccountStanding`) - pinned by `StandingLive.test.ts`. */
    public static readonly Ok: string = "ok";
    public static readonly Limited: string = "limited";
    public static readonly TempBanned: string = "temporarily-banned";
    public static readonly Locked: string = "locked";
    public static readonly Banned: string = "banned";
    public static readonly Outdated: string = "client-outdated";
    /** Every state with its own sentence here, `ok` first. */
    public static readonly States: string[] = ["ok", "limited", "temporarily-banned", "locked", "banned", "client-outdated"];
    /** The banner's tones (classes `st-danger` / `st-warning` in app.css) and roles. */
    public static readonly Danger: string = "danger";
    public static readonly Warning: string = "warning";
    public static readonly Alert: string = "alert";
    public static readonly Status: string = "status";

    /** A state word as the wire may carry it. DOCUMENTED EXCEPTION: pure pattern test. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static StateOk = (Raw: unknown): boolean => {
        return typeof Raw === "string" && /^[a-z][a-z-]{0,31}$/.test(Raw) === true;
    };

    /** An end time as the wire may carry it: a short string a Date can read. DOCUMENTED EXCEPTION: pure test.
     *  ERROR-SUPPRESSED-JUSTIFIED: cannot throw (`Date.parse` answers NaN). */
    public static UntilOk = (Raw: unknown): boolean => {
        return typeof Raw === "string" && /^[0-9TZ:.+-]{10,40}$/.test(Raw) === true && isNaN(Date.parse(Raw)) === false;
    };

    /**
     * Read `{state, until?}` (the answer's `standing`, or the service's own `AccountStanding.Json()`), or null when it is
     * not one. ⛔ Each field with its own test; everything else in the object (`why`, `since`) is ignored.
     */
    public static Parse = (Raw: unknown): AppStandingNow | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { return null; }
            const Doc: Record<string, unknown> = Raw as Record<string, unknown>;
            if (AppStanding.StateOk(Doc.state) === false) { return null; }
            const Out: AppStandingNow = new AppStandingNow();
            Out.State = Doc.state as string;
            if (Out.State !== AppStanding.Ok && AppStanding.UntilOk(Doc.until) === true) { Out.Until = Doc.until as string; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppStanding.Parse");
            return null;
        }
    };

    /** What the service puts on the wire. ⛔ A state word and an end time - nothing else exists to send. */
    public static Wire = (Now: AppStandingNow): Record<string, string> => {
        const Out: Record<string, string> = { state: AppStanding.Ok };
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            Out.state = Now.State;
            if (Now.Until.length > 0) { Out.until = Now.Until; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppStanding.Wire");
            return { state: AppStanding.Ok };
        }
    };

    /** One string for "what is shown": the state, and its end time when there is one. Both halves compare these. */
    public static SigOf = (Now: AppStandingNow): string => {
        if (getCoreUtils().KErrorMode === true) { return AppStanding.Ok; }
        try {
            if (Now.Until.length === 0) { return Now.State; }
            return Now.State + "@" + Now.Until;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppStanding.SigOf");
            return AppStanding.Ok;
        }
    };

    /** The ask's `standing` (a signature the page made with `SigOf`), or null = the page did not say what it shows. */
    public static SigIn = (Raw: unknown): string | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (typeof Raw !== "string" || Raw.length > 80) { return null; }
            const Parts: string[] = Raw.split("@");
            if (Parts.length > 2 || AppStanding.StateOk(Parts[0]) === false) { return null; }
            if (Parts.length === 2 && AppStanding.UntilOk(Parts[1]) === false) { return null; }
            return Raw;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppStanding.SigIn");
            return null;
        }
    };

    /** An end time the page may ECHO though it cannot read it: short, printable, no space, no "@". DOCUMENTED EXCEPTION:
     *  pure pattern test. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static EchoUntilOk = (Raw: unknown): boolean => {
        return typeof Raw === "string" && /^[\x21-\x3f\x41-\x7e]{1,40}$/.test(Raw) === true;
    };

    /**
     * ⛔ VERSION SKEW (2026-10-01): the signature of the standing the page RECEIVED - the service's own `state` and
     * `until`, as sent - or null when the answer carried none. The page ECHOES this; it DRAWS `Parse`'s reading. They
     * differ only when the service sent an end time this build's `UntilOk` does not read (a newer service): echoing
     * `SigOf(Parse(..))` then dropped the end time, the service's signature never matched, and every list ask was
     * answered at once. For anything this build reads, `EchoOf(Raw) === SigOf(Parse(Raw))` (pinned by a test).
     */
    public static EchoOf = (Raw: unknown): string | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Read: AppStandingNow | null = AppStanding.Parse(Raw);
            if (Read === null) { return null; }
            const Now: AppStandingNow = Read as AppStandingNow;
            const Until: unknown = (Raw as Record<string, unknown>).until;
            if (Now.State === AppStanding.Ok || AppStanding.EchoUntilOk(Until) === false) { return Now.State; }
            return Now.State + "@" + (Until as string);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppStanding.EchoOf");
            return null;
        }
    };

    /** A signature the page may SEND: its own (`SigIn`), or an echo of what it received (`EchoOf`). */
    public static EchoOk = (Raw: unknown): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (typeof Raw !== "string" || Raw.length > 80) { return false; }
            const Parts: string[] = Raw.split("@");
            if (Parts.length > 2 || AppStanding.StateOk(Parts[0]) === false) { return false; }
            return Parts.length === 1 || AppStanding.EchoUntilOk(Parts[1]) === true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppStanding.EchoOk");
            return false;
        }
    };

    /**
     * ⛔ THE SERVICE'S HALF OF THE SAME GUARD: does the page already show this standing? Yes when the signatures are
     * equal - and ALSO when the ask names the same state with NO end time. A page that cannot hold the end time the
     * service sends (any build, any reason) would otherwise be answered at once for ever; the end time still reaches a
     * page that can read it with the next answer (at the latest the hold's own deadline). A different state, or two
     * different end times, is never "shown".
     */
    public static ShownBy = (AskSig: string, Now: AppStandingNow): boolean => {
        if (getCoreUtils().KErrorMode === true) { return true; }
        try {
            if (AppStanding.SigOf(Now) === AskSig) { return true; }
            return AskSig === Now.State;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppStanding.ShownBy");
            return true;
        }
    };

    /** Nothing to show. DOCUMENTED EXCEPTION: a comparison. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static IsOk = (Now: AppStandingNow): boolean => {
        return Now.State === AppStanding.Ok;
    };

    /**
     * How loud: nothing is sent or received (banned, locked, temporarily banned) = danger + `role="alert"`; the account
     * still works in part, or mends itself (limited, the app version, a state this build does not know) = warning +
     * `role="status"`. DOCUMENTED EXCEPTION: pure lookup. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public static ToneOf = (State: string): string => {
        if (State === AppStanding.Banned || State === AppStanding.Locked || State === AppStanding.TempBanned) { return AppStanding.Danger; }
        return AppStanding.Warning;
    };

    /** DOCUMENTED EXCEPTION: pure lookup. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static RoleOf = (State: string): string => {
        if (AppStanding.ToneOf(State) === AppStanding.Danger) { return AppStanding.Alert; }
        return AppStanding.Status;
    };

    /** The browser's own clock and language: "Thu 2 Oct, 14:30". DOCUMENTED EXCEPTION: one formatting call.
     *  ERROR-SUPPRESSED-JUSTIFIED: a finite time cannot make `toLocaleString` throw, and `UntilWords` passes only those. */
    public static LocalTime = (Ms: number): string => {
        return new Date(Ms).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    };

    /** " until Thu 2 Oct, 14:30" - or "" when no end time is known or it has already passed. */
    public static UntilWords = (Until: string, NowMs: number, Format: (Ms: number) => string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (AppStanding.UntilOk(Until) === false) { return ""; }
            const Ms: number = Date.parse(Until);
            if (Ms <= NowMs) { return ""; }
            return " until " + Format(Ms);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppStanding.UntilWords");
            return "";
        }
    };

    /**
     * ⭐ THE SENTENCES. Plain words: what it means, then what the subscriber can do. `Until` = `UntilWords`' text ("" or
     * " until ..."). ⛔ Never a number, never a code. A state with no sentence of its own gets the general one.
     * ⛔ ONLY WHAT THE SERVICE DOES (`MessageBridge.OnConnectionClosed`): after 402 / 403 / 406 it tries again every
     * `MaxBackoffMs`; after 405 it asks for the current WhatsApp Web version and tries again. Whether WhatsApp then lets
     * it in is WhatsApp's decision - never "connects again by itself" for a ban or a lock (pinned by a test).
     */
    public static TextFor = (State: string, Until: string): AppStandingText => {
        const Out: AppStandingText = new AppStandingText();
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            if (State === AppStanding.Ok) { return Out; }
            if (State === AppStanding.Limited) { return AppStanding.Say("WhatsApp has limited your account",
                "You cannot start chats with people who have not written to you first" + Until + ". You still receive messages"
                + " and can reply in your existing chats. WhatsApp lifts this by itself - until then, avoid writing to new people."); }
            if (State === AppStanding.TempBanned) { return AppStanding.Say("WhatsApp has temporarily blocked your account",
                "Nothing is sent or received" + Until + ". Open WhatsApp on your phone to see what it says. WACommander"
                + " tries again every few minutes and carries on when the block ends."); }
            if (State === AppStanding.Locked) { return AppStanding.Say("WhatsApp has locked this connection",
                "WhatsApp is refusing WACommander as a linked device, so nothing is sent or received here. Open WhatsApp on"
                + " your phone and follow what it asks. WACommander keeps trying to connect in the meantime."); }
            if (State === AppStanding.Banned) { return AppStanding.Say("WhatsApp has banned your account",
                "Nothing is sent or received. Open WhatsApp on your phone - it shows the reason and lets you ask WhatsApp for"
                + " a review. Your archive here stays as it is."); }
            if (State === AppStanding.Outdated) { return AppStanding.Say("WhatsApp is not accepting the connection right now",
                "WhatsApp asked for a newer app version than WACommander announced. WACommander looks up the current version"
                + " and tries again by itself - you do not need to do anything. New messages appear once it is connected."); }
            return AppStanding.Say("WhatsApp reports a problem with your account",
                "Open WhatsApp on your phone to see what it says. Some messages may not be sent or received until it is resolved.");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppStanding.TextFor");
            return new AppStandingText();
        }
    };

    /** DOCUMENTED EXCEPTION: fills two fields. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    private static Say = (Title: string, Words: string): AppStandingText => {
        const Out: AppStandingText = new AppStandingText();
        Out.Title = Title;
        Out.Words = Words;
        return Out;
    };

    /**
     * ⛔ A REACH-OUT LIMIT PAST ITS OWN END TIME IS OVER (verifier, 2026-10-01): WhatsApp gave the time the limit ends, and
     * the service hears "not active" only when Baileys next asks (after a refused send) - so without this a limit that
     * simply ran out left its band (minus the time) on the page for ever. Only `limited`: a temporary ban past its time
     * stays until the login that lifts it (the connection is still closed until then). ⚠ What is DRAWN, not what is
     * echoed: the ask still carries the service's own signature, so nothing loops.
     */
    public static IsOver = (Now: AppStandingNow, NowMs: number): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Now.State !== AppStanding.Limited || AppStanding.UntilOk(Now.Until) === false) { return false; }
            return Date.parse(Now.Until) <= NowMs;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppStanding.IsOver");
            return false;
        }
    };

    /** The banner's words for a standing at a moment, in the reader's own clock. */
    public static Text = (Now: AppStandingNow, NowMs: number, Format: (Ms: number) => string): AppStandingText => {
        if (getCoreUtils().KErrorMode === true) { return new AppStandingText(); }
        try {
            if (AppStanding.IsOver(Now, NowMs) === true) { return new AppStandingText(); }
            return AppStanding.TextFor(Now.State, AppStanding.UntilWords(Now.Until, NowMs, Format));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppStanding.Text");
            return new AppStandingText();
        }
    };
}

/** The banner's markup. ⛔ No inline style (the CSP); the words are escaped; the icon is a constant of ours. */
export class AppStandingView {
    public static readonly Id: string = "wac-standing";
    public static readonly BaseClass: string = "standing";

    /** The banner's class for a state: `standing st-danger` / `standing st-warning`. */
    public static ClassOf = (State: string): string => {
        if (getCoreUtils().KErrorMode === true) { return AppStandingView.BaseClass; }
        try {
            return AppStandingView.BaseClass + " st-" + AppStanding.ToneOf(State);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppStandingView.ClassOf");
            return AppStandingView.BaseClass;
        }
    };

    /** The inside of the banner: the warning icon, the title, the sentence. "" for nothing to say. */
    public static Html = (Text: AppStandingText): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Text.Title.length === 0) { return ""; }
            return "<div class=\"stin\"><span class=\"stico\" aria-hidden=\"true\">" + AppIcons.Svg(AppIcons.Warning, 18) + "</span>"
                + "<p class=\"stbody\"><strong class=\"sttitle\">" + kHTMLCore.EscapeHtml(Text.Title) + ".</strong> "
                + "<span class=\"stwords\">" + kHTMLCore.EscapeHtml(nz(Text.Words)) + "</span></p></div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppStandingView.Html");
            return "";
        }
    };
}
